import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { find } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getBookings } from '../../../services';
import { addOffering, addToastInfo, selectCommon } from '../../../store/common';
import {
  createBooking,
  fetchBookingsHistory,
  selectFilters,
} from '../../../store';
import { EventType } from '../../../types';
import { selectUpcomingEvents } from '../../../store/upcomingEvents';
import useSearchParams from '../../shared/hook/useSearchParams';
import useRedirectToBookings from './use-redirect-to-bookings';
import useDrawerScroll from '../../shared/hook/use-drawer-scroll';
import useGetEvents from '../../shared/hook/use-get-events';

const useOfferingDrawer = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const searchparams = useSearchParams();
  const { offering } = useSelector(selectCommon);
  const { scrolled, onScroll } = useDrawerScroll({
    resetBy: offering?.id,
  });
  const { data: myBookings } = useQuery(['my-bookings'], getBookings);

  const { recommendationId, communitySessions } = useGetEvents();
  const { upcomingEvents } = useSelector(selectUpcomingEvents);
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { refetch: refetchMyBookings } = useQuery(['my-bookings'], getBookings);
  const isBooked = Boolean(
    find(myBookings, (item) => item.offeringId === offering?.id),
  );
  const filters = useSelector(selectFilters);
  const utmCampaign = searchparams.get('utm_campaign') ?? 'WEBAPP';

  const isRecommendedEvent = Boolean(
    communitySessions.find(({ id }) => id === offering?.id)?.isRecommended,
  );

  useEffect(() => {
    const isCalendlyScheduledEvent = (e: any) => {
      return (
        e.data.event &&
        e.data.event.indexOf('calendly') === 0 &&
        e.data.event === 'calendly.event_scheduled'
      );
    };

    const onMessage = (e: any) => {
      if (isCalendlyScheduledEvent(e)) {
        onClose();

        refetchMyBookings();
        dispatch(fetchBookingsHistory());
        dispatch(
          addToastInfo({
            toastInfo: {
              open: true,
              type: 'success',
              content: t('v2.global.snackbar_label_successfulBooking'),
            },
          }),
        );

        history.push('/bookings');
      }
    };

    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const offeringId = params.get('offeringId');

    if (offeringId && upcomingEvents.length) {
      const offering = upcomingEvents.filter((item) => item.id === offeringId);

      if (offering.length) {
        dispatch(addOffering({ offering: offering[0] }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, upcomingEvents]);

  const onClose = () => {
    setPage(0);

    dispatch(addOffering({}));
    const params = new URLSearchParams(location.search);
    const offeringId = params.get('offeringId');

    if (offeringId) {
      history.replace({
        pathname: window.location.pathname,
        search: '',
      });
    }
  };

  useRedirectToBookings({ onClose });

  const onConfirm = () => {
    if (offering?.type === EventType.WEBINAR) {
      dispatch(
        createBooking({
          offeringId: offering.id,
          ...(isRecommendedEvent && { recommendationId }),
          filters,
          utmCampaign,
        }),
      );
    } else {
      setPage(1);
    }
  };

  const onBack = () => {
    if (location.previous && page === 0) {
      history.push(location.previous);
      dispatch(addOffering({}));
    }
    setPage(0);
  };

  const ctaLabel =
    offering && offering.type === EventType.WEBINAR
      ? t('v2.global.button_label_registerForWebinar')
      : t('v2.global.button_label_enterDetailsToBook');

  const showBack = page === 1 || location.previous;
  const showCta = page === 0;

  const { maxParticipants = 0, numberOfBookings = 0 } = offering ?? {};
  const isFullyBooked = maxParticipants <= numberOfBookings;

  const ctaDisabled = isBooked || isFullyBooked;
  const ctaSecondary =
    isBooked && t('v2.offeringOverview.drawer_label_button_manageBooking');
  const ctaSecondaryCallback = () => history.push('/bookings');

  return {
    onScroll,
    scrolled,
    offering,
    onClose,
    showCta,
    showBack,
    ctaLabel,
    ctaDisabled,
    page,
    onBack,
    onConfirm,
    ctaSecondary,
    ctaSecondaryCallback,
  };
};

export default useOfferingDrawer;
